import { useState, useRef } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_HEADERS = [
  "vin", "year", "make", "model", "trim", "mileage", "mmr_current",
  "comp_retail_avg", "cost_to_market_pct", "market_days_supply",
  "avg_retail_days_to_sell", "recon_estimate", "mmr_trend_30d",
  "source_detail", "rooftop_id",
];

export default function UploadReport() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/upload-vauto-report", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Processed ${r.data.rows_processed} rows · ${r.data.strong_buys_flagged} strong buys flagged`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = SAMPLE_HEADERS.join(",") + "\n" +
      "1FT7W2BT5NEF12345,2022,Ford,F-150,XLT SuperCrew,28500,42000,51200,93.5,52,32,1400,380,Manheim DC lane 14,rt_for_alexan\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "freddy-vauto-template.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Layout variant="corporate">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <Upload size={12} /> Daily ingestion
        </div>
        <h1 className="text-3xl font-bold text-navy mt-1">Upload vAuto report</h1>
        <p className="text-sm text-slate-500 mt-1 font-medium max-w-2xl">
          Drop your daily vAuto / Mannheim export here. Freddy scores every VIN against your rooftop fit, velocity, and margin targets in under a second.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div
            data-testid="upload-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
            }}
            className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-freddy-red transition-colors cursor-pointer"
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              data-testid="upload-file-input"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <div className="mx-auto w-14 h-14 rounded-full bg-navy text-white flex items-center justify-center mb-4">
              <FileSpreadsheet size={22} />
            </div>
            <div className="font-bold text-navy text-lg">Drag .csv or .xlsx here</div>
            <div className="text-sm text-slate-500 mt-1">or click to browse your files</div>
            {file && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 text-sm font-semibold text-navy">
                <CheckCircle2 size={14} className="text-verdict-strongText" />
                {file.name}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              data-testid="upload-submit"
              disabled={!file || uploading}
              onClick={upload}
              className="bg-freddy-red hover:bg-freddy-redDark text-white font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-md disabled:opacity-50 inline-flex items-center gap-2"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Score this batch
            </button>
            <button
              data-testid="download-template"
              onClick={downloadTemplate}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-navy font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md inline-flex items-center gap-2"
            >
              <Download size={12} /> Download CSV template
            </button>
          </div>

          {result && (
            <div className="mt-6 bg-white border border-verdict-strong/30 bg-verdict-strong/5 rounded-xl p-5" data-testid="upload-result">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-verdict-strongText" />
                <h3 className="font-bold text-navy">Batch processed</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Rows processed" value={result.rows_processed} />
                <Stat label="Inserted" value={result.inserted} />
                <Stat label="Updated" value={result.updated} />
                <Stat label="Strong buys flagged" value={result.strong_buys_flagged} color="text-freddy-red" />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-bold text-navy mb-3">Expected columns</h3>
          <p className="text-xs text-slate-500 mb-3 font-medium">Case-insensitive headers. Extra columns are ignored.</p>
          <ul className="space-y-1.5 text-xs font-mono">
            {SAMPLE_HEADERS.map((h) => (
              <li key={h} className="flex items-center gap-2 text-slate-700">
                <span className="w-1 h-1 bg-freddy-red rounded-full" />{h}
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500 font-medium">
            <span className="font-bold text-navy">Tip:</span> upload once a day after your morning vAuto export. Overnight aging + MMR movement is picked up automatically.
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, color = "text-navy" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`text-xl font-bold tabular ${color}`}>{value}</div>
    </div>
  );
}
